import React from 'react';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const PagePreview = ({ blocks }) => {
  const renderBlock = (block) => {
    switch (block.type) {
      case 'hero':
        return (
          <div
            key={block.id}
            className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20 px-4 md:px-8"
            style={{
              backgroundImage: block.content.background_image 
                ? `url(${block.content.background_image})` 
                : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl font-bold mb-4">
                {block.content.title || 'Titre Hero'}
              </h1>
              <p className="text-2xl mb-6">
                {block.content.subtitle || 'Sous-titre'}
              </p>
              {block.content.description && (
                <div 
                  className="text-lg mb-8"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.content.description) }}
                />
              )}
              {block.content.cta_text && (
                <a 
                  href={block.content.cta_url || '#'}
                  className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                >
                  {block.content.cta_text}
                </a>
              )}
            </div>
          </div>
        );

      case 'text':
        return (
          <div key={block.id} className="py-12 px-4 md:px-8">
            <div className="max-w-4xl mx-auto">
              {block.content.title && (
                <h2 className="text-3xl font-bold mb-6">{block.content.title}</h2>
              )}
              {block.content.text && (
                <div 
                  className="prose max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.content.text) }}
                />
              )}
            </div>
          </div>
        );

      case 'image':
        return (
          <div key={block.id} className="py-8 px-4 md:px-8">
            <div className="max-w-4xl mx-auto">
              {block.content.image_url && (
                <img
                  src={block.content.image_url}
                  alt={block.content.alt || 'Image'}
                  className="w-full rounded-lg shadow-lg"
                />
              )}
              {block.content.caption && (
                <p className="text-center text-gray-600 mt-4">
                  {block.content.caption}
                </p>
              )}
            </div>
          </div>
        );

      case 'stats':
        return (
          <div key={block.id} className="py-16 px-8 bg-gray-50">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {(block.content.items || []).map((item, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-5xl font-bold text-blue-600 mb-2">
                      {item.value}
                    </div>
                    <div className="text-xl text-gray-600">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'text_image':
        const isImageLeft = block.content.image_position === 'left';
        return (
          <div key={block.id} className="py-16 px-4 md:px-8">
            <div className="max-w-6xl mx-auto">
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${isImageLeft ? 'flex-row-reverse' : ''}`}>
                <div className={isImageLeft ? 'md:order-2' : ''}>
                  {block.content.title && (
                    <h2 className="text-3xl font-bold mb-4">{block.content.title}</h2>
                  )}
                  {block.content.text && (
                    <div 
                      className="prose text-gray-700"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.content.text) }}
                    />
                  )}
                </div>
                <div className={isImageLeft ? 'md:order-1' : ''}>
                  {block.content.image_url ? (
                    <img
                      src={block.content.image_url}
                      alt={block.content.alt || 'Image'}
                      className="w-full rounded-lg shadow-lg"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-gray-400">Image</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'card':
        return (
          <div key={block.id} className="py-12 px-4 md:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-4 md:p-8">
                {block.content.title && (
                  <h3 className="text-2xl font-bold mb-4">{block.content.title}</h3>
                )}
                {block.content.text && (
                  <div 
                    className="prose text-gray-700"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.content.text) }}
                  />
                )}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div key={block.id} className="py-8 px-4 md:px-8">
            <div className="max-w-4xl mx-auto bg-gray-100 rounded-lg p-4 md:p-8">
              <p className="text-gray-500">Bloc {block.type} non supporté en prévisualisation</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="bg-white">
      {blocks.length === 0 ? (
        <div className="py-20 text-center text-gray-500">
          <p>Aucun bloc à prévisualiser</p>
        </div>
      ) : (
        blocks.map(block => renderBlock(block))
      )}
    </div>
  );
};

export default PagePreview;
